import filters from 'config/algolia/filters';
import sorts from 'config/algolia/sorts';
import FilterContainer from '@sportson/core-web/containers/FilterContainer';
import useAppSelector from '@sportson/core-web/hooks/useAppSelector';
import useSaveResponse from '@sportson/core-web/hooks/useSaveResponse';
import CmsModules from '@sportson/core-web/libs/wordpress/content/CmsModules';
import React, { useEffect, useRef, useState } from 'react';
import { shallowEqual } from 'react-redux';
import Pagination from 'components/Pagination';
import CategoryModule from 'components/Category/Category';
import CategoryHeader from 'components/Category/CategoryHeader';
import styled from 'libs/styled';
import convertCmsFiltersToFilter from '@sportson/core-web/utils/convertCmsFiltersToFilter';
import { transformWordpressProductSelectionToAlgoliaParameters } from 'libs/wordpress/utils/transformWordpressProductSelectionToAlgoliaParameters';
import { inServer } from '@sportson/core-web/constants';
import Breadcrumbs from 'components/Breadcrumbs';

const Wrapper = styled('div')``;

const Category = () => {
    const wrapperRef = useRef({});
    const title = useAppSelector(({ page }) => page?.data?.title, shallowEqual);
    const { description } = useAppSelector(({ page }) => page?.data?.settings?.categoryHeader, shallowEqual) || {};
    const sizeGuideButtons = useAppSelector(({ page }) => page?.data?.settings?.sizeGuideButtons, shallowEqual) || {};
    const pageContent = useAppSelector(({ page }) => page?.data?.pageContent, shallowEqual);
    const childrens = useAppSelector(({ page }) => page?.data?.children, shallowEqual);
    const parentCategory = useAppSelector(({ page }) => page?.data?.parent, shallowEqual);
    const productFilters = useAppSelector(
        ({ page }) => page?.data?.productGrid?.productGrid?.productFilters,
        shallowEqual,
    );
    const productSelection = useAppSelector(
        ({ page }) => page?.data?.productGrid.productGrid.productSelection,
        shallowEqual,
    );
    const articleTitle = useAppSelector(({ page }) => page?.data?.title, shallowEqual);
    const summary = useAppSelector(({ page }) => page?.data?.articleSummary?.summary, shallowEqual ?? []);
    const altTitle = useAppSelector(({ page }) => page?.data?.articleSummary?.alternativeTitle, shallowEqual ?? []);
    const tag = useAppSelector(({ page }) => page?.data?.categories[0], shallowEqual) ?? [];
    const breadcrumbs = useAppSelector(({ page }) => page?.data?.breadcrumbs, shallowEqual) ?? [];
    const category = useAppSelector(({ page }) => page?.data.categories[0], shallowEqual) ?? '';

    const cmsSelection = transformWordpressProductSelectionToAlgoliaParameters(productSelection)?.default || {};
    const cmsFilters = convertCmsFiltersToFilter(productFilters);
    const mergedFilters = { ...filters, ...cmsFilters };

    // Save response from container
    const [savedResponse, saveResponse] = useSaveResponse();
    const [currentPage, setCurrentPage] = useState(0);
    const [pageCount, setPageCount] = useState(0);
    const [currentSort, setCurrentSort] = useState(null);

    const productsPerPage = 20;

    const scrollToTop = (next, smooth = false) => {
        // Scroll back to top of wrapper on pagination
        const headerHeight =
            (!inServer && getComputedStyle(document.documentElement).getPropertyValue('--header-height')) || 0;

        setCurrentPage(next);
        if (wrapperRef && wrapperRef.current) {
            scrollTo({
                smooth,
                top: wrapperRef.current.offsetTop - headerHeight,
            });
        }
    };

    const articleData = {
        tag,
        title: altTitle || articleTitle,
        summary,
    };

    const breadcrumbData = [
        ...breadcrumbs.map((b) => ({ permalink: b.permalink, title: b.title })),
        ...(category
            ? [{ permalink: `${breadcrumbs[breadcrumbs.length - 1]?.permalink}?category=${category}`, title: category }]
            : []),
        { permalink: null, title: articleData.title },
    ];

    useEffect(() => {
        const url = new URL(window.location.href);
        url.searchParams.delete('page');
        window.history.replaceState({}, '', url.toString());
        scrollToTop(0);
    }, [currentSort]);

    return (
        <>
            <Breadcrumbs
                gridColumn="standard"
                p={['16px 24px 0 12px', null, null, null, null, '16px 40px 0 40px']}
                breadcrumbs={breadcrumbData}
            />
            <Wrapper ref={wrapperRef}>
                <CategoryHeader
                    heading={title}
                    description={description}
                    parentCategory={parentCategory}
                    subCategories={childrens}
                    sizeGuideButtons={sizeGuideButtons}
                />
                <FilterContainer
                    config={mergedFilters}
                    filters={cmsSelection}
                    pageSize={productsPerPage}
                    pagination={currentPage}
                    sorts={sorts}
                    render={(props) => {
                        const { response } = props;
                        saveResponse(response);
                        response && setPageCount(response.pageCount);
                        response && setCurrentPage(response.page);

                        if (
                            response &&
                            response.sorts &&
                            currentSort !== response.sorts.find((sort) => sort.selected)?.index
                        ) {
                            setCurrentSort(response.sorts.find((sort) => sort.selected)?.index);
                        }

                        return (
                            savedResponse.current.products && (
                                <CategoryModule
                                    config={mergedFilters}
                                    response={savedResponse.current}
                                    sizeGuideButtons={sizeGuideButtons}
                                />
                            )
                        );
                    }}
                />
                {pageCount !== 0 && (
                    <Pagination
                        handleClick={scrollToTop}
                        page={currentPage}
                        pageCount={pageCount}
                        pageSize={productsPerPage}
                    />
                )}
                {pageContent && <CmsModules data={pageContent} />}
            </Wrapper>
        </>
    );
};
export default Category;
